var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import Icon from '../Icon';
import { FocusStyles } from '../../styles/FocusStyles';
import { useIsMobileNavigation } from '../MainNavigation/hooks/useIsMobileNavigation';
import { OmniButton } from '../OmniButton';
import { AssistiveTechnologyOnly } from '../../styles/AssistiveTechnologyOnly';
const Wrapper = styled.nav `
  display: ${props => (props.isVisible ? 'block' : 'none')};
  height: 3.125rem;
  background: ${props => props.theme.MetaNavBackground};
`;
const Inner = styled.div `
  display: flex;
  justify-content: flex-end;
  height: 100%;
  max-width: 75rem; /* 1200px */
  margin-left: auto;
  margin-right: auto;
  padding: 0 1rem;
`;
const VisuallyHiddenLabel = styled.h2 `
  ${AssistiveTechnologyOnly}
`;
const ID_META_NAVIGATION_LABEL = 'meta-navigation-label';
function InnerMetaNavigation({ children, dataTestId = undefined }, ref) {
    const { t } = useTranslation('MetaNavigation');
    const isMobile = useIsMobileNavigation();
    return (React.createElement(Wrapper, { "aria-labelledby": ID_META_NAVIGATION_LABEL, "data-testid": dataTestId, isVisible: !isMobile, ref: ref },
        React.createElement(VisuallyHiddenLabel, { id: ID_META_NAVIGATION_LABEL }, t('MetaNavigationLabel')),
        React.createElement(Inner, null, children)));
}
/**
 * The ref is required to calculate the height of this component from the
 * outside to make the special sticky behavior work.
 */
export const MetaNavigation = forwardRef(InnerMetaNavigation);
const linkCss = css `
  display: flex;
  align-content: center;
  justify-content: center;
  align-items: center;
  justify-items: center;
  margin-left: 2rem;
  border-top: 0.125rem solid
    ${props => (props.isActive ? props.theme.BrandPrimary : 'transparent')};
  border-bottom: 0.125rem solid transparent;
  ${FocusStyles}

  /* for OmniButton-Style */
  &,
  > span > span {
    display: inline-flex;
    height: 100%;
    align-items: center;
    color: ${props => props.isActive ? props.theme.BrandPrimary : props.theme.Gray};
    text-decoration: none;
    font-family: ${props => props.theme.fonts.serif};
    font-size: 0.75rem;
    font-weight: 400;
    padding: 0;
    transition: color 0.3s linear;

    &:hover {
      color: ${props => props.theme.BrandPrimary};
    }
  }
`;
const ExternalLink = styled.a `
  ${linkCss}
`;
export function MetaNavigationLinkExternal({ href, dataTestId = undefined, isActive = false, children, rel = undefined, onClick = undefined, }) {
    return (React.createElement(ExternalLink, { href: href, isActive: isActive, rel: rel, "data-testid": dataTestId, onClick: onClick }, children));
}
const InternalLink = styled((_a) => {
    var { isActive } = _a, rest = __rest(_a, ["isActive"]);
    return (React.createElement(OmniButton, Object.assign({}, rest)));
}) `
  ${linkCss}
`;
export function MetaNavigationLinkInternal({ to, dataTestId = undefined, isActive = false, children, onClick, }) {
    return to ? (React.createElement(InternalLink, { isActive: isActive, href: to, dataTestId: dataTestId }, children)) : onClick ? (React.createElement(InternalLink, { isActive: isActive, onClick: onClick, dataTestId: dataTestId }, children)) : null;
}
export const MetaNavIcon = styled(Icon) `
  width: 1rem;
  height: 1rem;
  margin-right: 0.25rem;
`;
export const MetaNavigationLanguageWrapper = styled.span `
  ${linkCss};

  margin-left: 4rem;
`;
